import React, { useEffect } from 'react';
import { Box, Button, Divider, IconButton, Modal, Paper, Stack, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import CustomTextInput from '../../../components/CustomTextInput';
import SelectedItems from '../../../components/SelectedItems';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { useCreateUserHandler } from '../dataHandlers/createUserHandler';
import { useUpdateUserHandler } from '../dataHandlers/updateUserHandler';
import { useGetRolesHandler } from '../dataHandlers/getRolesHandler';
import { useGetUserHandler } from '../dataHandlers/getUserHandler';
import RoleSelect from './RoleSelect';

export default function InviteUserDialog({ open, onClose, user, mode }) {
  const { settings, common } = useTranslatedMessages();
  const team = settings.team ?? {};
  const ui = common.ui ?? {};
  const modal = team.modal ?? {};

  const schema = Yup.object().shape({
    name: Yup.string().required(settings.validations?.field_required || 'This field is required'),
    email: Yup.string()
      .email(settings.validations?.invalid_email || 'Email must be a valid email')
      .required(settings.validations?.field_required || 'This field is required'),
    role_ids: Yup.array().of(Yup.string()),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { name: '', email: '', role_ids: [] },
  });

  const { mutate: createUser, isPending: isCreating } = useCreateUserHandler({ onSuccess: onClose });
  const { mutate: updateUser, isPending: isUpdating } = useUpdateUserHandler({ onSuccess: onClose });
  const { roles, isLoading: isRolesLoading } = useGetRolesHandler({
    searchQuery: { page: 0, size: 100 },
    enabled: open,
  });
  const { user: userDetails, isLoading: isUserLoading } = useGetUserHandler({
    userId: user?.id,
    enabled: open && mode === 'edit' && Boolean(user?.id),
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    if (mode === 'edit' && userDetails) {
      reset({
        name: userDetails.name || '',
        email: userDetails.email || '',
        role_ids: (userDetails.role_ids ?? []).map(String),
      });
      return;
    }

    if (mode === 'create') {
      reset({
        name: '',
        email: '',
        role_ids: [],
      });
    }
  }, [open, mode, userDetails, reset]);

  const onSubmit = (data) => {
    if (mode === 'edit' && user?.id) {
      updateUser({ userId: user.id, name: data.name, role_ids: data.role_ids });
      return;
    }
    createUser({ name: data.name, email: data.email, role_ids: data.role_ids });
  };

  const isPending = isCreating || isUpdating;
  const isFormLoading = mode === 'edit' && isUserLoading;
  const title = mode === 'edit' ? modal.edit_title || 'Edit User' : modal.invite_title || 'Invite User';
  const submitLabel = isPending
    ? ui.processing || 'Processing...'
    : mode === 'edit'
      ? modal.submit_save || 'Save'
      : modal.submit_invite || 'Invite User';

  return (
    <Modal open={open} onClose={onClose}>
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          p: 2,
          outline: 'none',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
        <Divider sx={{ my: 1 }} />
        <Stack component="form" spacing={2} sx={{ mt: 2 }} onSubmit={handleSubmit(onSubmit)}>
          <CustomTextInput name="name" label={ui.name || 'Name'} control={control} errors={errors} />
          <CustomTextInput
            name="email"
            label={ui.email || 'Email'}
            control={control}
            errors={errors}
            disabled={mode === 'edit'}
          />
          <Controller
            name="role_ids"
            control={control}
            render={({ field }) => (
              <Box>
                <Typography mb={1}>{modal.roles_label || 'Roles'}</Typography>
                <RoleSelect
                  roles={roles}
                  selected={field.value ?? []}
                  isLoading={isRolesLoading || isFormLoading}
                  onChange={field.onChange}
                  placeholder={modal.roles_placeholder || 'Select roles'}
                  selectedCountLabel={modal.roles_selected}
                />
                <SelectedItems
                  items={field.value ?? []}
                  getLabel={(id) => roles.find((role) => role.id === id)?.name ?? id}
                  onRemove={(id) => field.onChange((field.value ?? []).filter((value) => value !== id))}
                />
              </Box>
            )}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="submit" variant="contained" disabled={isPending || isFormLoading}>
              {submitLabel}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Modal>
  );
}
